import classNames from 'classnames';
import React, { useState } from 'react';
import Image from '@/components/atoms/image/image';
import RichText from '@/components/atoms/rich-text/rich-text';
import Typography from '@/components/atoms/typography/typography';
import HubspotFormMulti from '@/components/modules/hubspot-form/hubspot-form-multi/hubspot-form-multi';
import Button from '@/components/molecules/button/button';
import Column from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import Lightbox from '@/components/utilities/lightbox/lightbox';
import ModuleWrapper, {
  ModuleWrapperProps,
} from '@/components/utilities/module-wrapper/module-wrapper';
import Row from '@/components/utilities/row/row';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import HubspotFormItem from './hubspot-form-item/hubspot-form-item';
import styles from './hubspot-form.module.scss';

export interface HubspotFormProps extends ModuleWrapperProps {
  /** Indicates where to align the text */
  alignment?: Content.Components['HubspotFormModuleDto']['alignment'];
  /** The text to show in the button */
  buttonText: Content.Components['HubspotFormModuleDto']['buttonText'];
  /** The forms to show */
  forms: Content.Components['HubspotFormModuleDto']['forms'];
  /** The text to show as a headline */
  headline?: Content.Components['HubspotFormModuleDto']['headline'];
  /** Whether to show the image on mobile */
  hideImageOnMobile?: Content.Components['HubspotFormModuleDto']['hideImageOnMobile'];
  /** Whether to show the rich text on mobile */
  hideRichTextOnMobile?: Content.Components['HubspotFormModuleDto']['hideRichTextOnMobile'];
  /** The image to show */
  image: Content.Components['HubspotFormModuleDto']['image'];
  /** Indicates whether to show the form in a lightbox */
  lightbox?: Content.Components['HubspotFormModuleDto']['lightbox'];
  /** The text to show as a pre headline */
  preHeadline?: Content.Components['HubspotFormModuleDto']['preHeadline'];
  /** The text to show as a sub headline */
  subHeadline?: Content.Components['HubspotFormModuleDto']['subHeadline'];
  /** The text to show as a description */
  text?: Content.Components['HubspotFormModuleDto']['text'];
  /** The variant of the component to use */
  variant?: Content.Components['HubspotFormModuleDto']['variant'];
  /** Indicates where to align the text */
  verticalAlignment: Content.Components['HubspotFormModuleDto']['verticalAlignment'];
}

const HubspotForm = ({
  alignment = 'left',
  buttonText,
  forms = [],
  headline,
  hideImageOnMobile = false,
  hideRichTextOnMobile = false,
  image,
  lightbox = false,
  preHeadline,
  subHeadline,
  text,
  variant,
  verticalAlignment,
  ...rest
}: HubspotFormProps) => {
  const isTablet = useWindowSize('tablet');
  const [isOpen, setIsOpen] = useState(false);

  const shouldHideImage = !lightbox && hideImageOnMobile && !isTablet;
  const shouldHideRichText = !lightbox && hideRichTextOnMobile && !isTablet;

  const renderImage = () => {
    if (!image) return;

    if (shouldHideImage) return;

    return (
      <Image
        alt={image.altText}
        aspectRatio={image.aspectRatio ?? 'threeByFour'}
        className={styles.image}
        fill
        focalPoint={image.focalPoint}
        src={image.url}
        unoptimized
        width={541}
      />
    );
  };

  const renderRichText = () => {
    if (!text) return;

    if (shouldHideRichText) return;

    return <RichText text={text} />;
  };

  const renderForms = (
    variant?: Content.Components['HubspotFormModuleDto']['variant'],
  ) => {
    if (!forms || !forms.length) return;

    const variantProp = variant ? { variant: variant } : {};

    if (forms.length === 1) {
      return (
        <HubspotFormItem
          form={forms[0]}
          {...variantProp}
          isLightbox={lightbox}
        />
      );
    }

    return (
      <HubspotFormMulti forms={forms} {...variantProp} isLightbox={lightbox} />
    );
  };

  return (
    <React.Fragment>
      <ModuleWrapper
        {...rest}
        className={classNames(styles.component, {
          [styles.isTopAligned]: verticalAlignment === 'top',
          [styles.isMiddleAligned]: verticalAlignment === 'middle',
          [styles.isBottomAligned]: verticalAlignment === 'bottom',
        })}
      >
        <Container>
          <Row className={styles.row}>
            <Column
              className={styles.textColumn}
              offset={{ laptop: alignment === 'right' ? 1 : 0 }}
              order={{ laptop: alignment === 'right' ? 2 : 1 }}
              width={{ tablet: !lightbox ? 5 : 6 }}
            >
              {preHeadline && (
                <Typography
                  className={styles.preHeadline}
                  tagStyle="labelLarge"
                >
                  {preHeadline}
                </Typography>
              )}

              {headline && (
                <Typography
                  className={styles.headline}
                  tagStyle="displayMedium"
                >
                  {headline}
                </Typography>
              )}

              {subHeadline && (
                <Typography
                  className={styles.subHeadline}
                  tagStyle="bodyLargeBold"
                >
                  {subHeadline}
                </Typography>
              )}

              {text && renderRichText()}

              {lightbox && (
                <Button
                  className={styles.button}
                  onClick={() => setIsOpen(true)}
                  text={buttonText}
                />
              )}

              {!lightbox && renderImage()}
            </Column>

            <Column
              offset={{ laptop: alignment === 'right' ? 0 : 1 }}
              order={{ laptop: alignment === 'right' ? 1 : 2 }}
              width={{ laptop: !lightbox ? 6 : 5 }}
            >
              {!lightbox ? renderForms(variant) : renderImage()}
            </Column>
          </Row>
        </Container>
      </ModuleWrapper>

      {lightbox && (
        <Lightbox isOpen={isOpen} onClose={() => setIsOpen(false)}>
          {renderForms()}
        </Lightbox>
      )}
    </React.Fragment>
  );
};

HubspotForm.displayName = 'HubspotForm';

export default HubspotForm;
